"use client";

import { useEffect, useId, useState } from "react";
import Link from "next/link";
import { HeroGlow, ReviewBadges } from "@/components/ui";
import { IdCard, ShieldCheck, Headphones } from "@/components/icons";
import { DOCS, ROUTES } from "@/lib/constants";
import { cn, scrollToId } from "@/lib/utils";
import MobileTierGrid from "@/components/sections/products/mobile-proxies/pricing-tiers/tier-grid";
import PerProxyCard from "./per-proxy-card";
import { SELECT_MOBILE_PLAN_EVENT, type MobilePlan } from "./plan-event";

const PLAN_OPTIONS: { id: MobilePlan; label: string }[] = [
  { id: "per-gb", label: "Per GB" },
  { id: "per-proxy", label: "Per Proxy" },
];

interface Props {
  asHero?: boolean;
  hideHeader?: boolean;
  defaultPlan?: MobilePlan;
}

export default function MobilePricingCards({
  asHero = false,
  hideHeader = false,
  defaultPlan = "per-gb",
}: Props) {
  const [plan, setPlan] = useState<MobilePlan>(defaultPlan);
  const tablistId = useId();
  const orderedOptions = [...PLAN_OPTIONS].sort(
    (a, b) => Number(b.id === defaultPlan) - Number(a.id === defaultPlan)
  );

  useEffect(() => {
    const handleSelect = (event: Event) => {
      const next = (event as CustomEvent<MobilePlan>).detail;
      if (next === "per-gb" || next === "per-proxy") setPlan(next);
    };
    window.addEventListener(SELECT_MOBILE_PLAN_EVENT, handleSelect);
    return () => window.removeEventListener(SELECT_MOBILE_PLAN_EVENT, handleSelect);
  }, []);

  const handleMoneyBackClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    window.location.hash = "money-back-guarantee";
    scrollToId("money-back-guarantee");
  };

  const sectionPadding = hideHeader
    ? "pt-0 pb-section"
    : asHero
      ? "pt-24 sm:pt-32 pb-16 sm:pb-24"
      : "py-section";
  // With hideHeader the cards sit flush at the top (pt-0); keep overflow visible so their hover
  // lift + shadow aren't clipped. Otherwise clip the Y axis only: this section sits inside the
  // shell's max-w-7xl frame, so clipping X as well cut the hero's full-bleed ribbon off at the
  // frame's vertical hairlines. HeroGlow's own wrapper already clips the ribbon.
  const overflow = hideHeader ? "overflow-visible" : "overflow-y-clip";

  return (
    <section
      id="pricing-plans"
      className={`full-bleed band-wash border-border relative ${overflow} border-b ${sectionPadding}`}
      {...(hideHeader
        ? { "aria-label": "Pricing plans" }
        : { "aria-labelledby": "pricing-cards-heading" })}
    >
      {asHero && <HeroGlow align="center" />}

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {!hideHeader && (
          <div className={`mx-auto max-w-2xl text-center ${asHero ? "mb-10" : "mb-12"}`}>
            {asHero ? (
              <div
                className="mb-6 flex flex-wrap items-center justify-center gap-3"
                aria-label="Review platforms"
              >
                <ReviewBadges />
              </div>
            ) : (
              <p className="text-muted-foreground mb-3 text-sm font-medium">Choose Your Plan</p>
            )}

            {asHero ? (
              <h1
                id="pricing-cards-heading"
                className="text-foreground mb-5 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
              >
                Mobile Proxy Pricing
                <br />
                Plans Starting at $2/GB
              </h1>
            ) : (
              <h2
                id="pricing-cards-heading"
                className="text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl"
              >
                Flexible mobile proxy pricing.
              </h2>
            )}

            <p className="text-muted-foreground light:text-foreground/80 text-lg leading-relaxed">
              {asHero
                ? "The only mobile proxies you'll ever need — premium, ethically sourced mobile IPs. Pay per GB or per proxy, scale as you go. No contracts, no hidden fees."
                : plan === "per-gb"
                  ? "Monthly bandwidth packages for premium pooled mobile IPs at a flat $2/GB, from $50 for 25 GB. Included bandwidth expires after one month."
                  : "Real mobile IPs at $59 per proxy per month, with unlimited bandwidth and 50 GB of prioritized data per proxy. Rotate, pool, and manage each exit yourself."}
            </p>

            {asHero && (
              <div
                className="mt-7 flex flex-wrap items-center justify-center gap-4"
                aria-label="Trust signals"
              >
                <Link
                  href={ROUTES.kyc}
                  className="border-border bg-muted/40 text-foreground hover:border-brand-green hidden items-center rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors md:flex"
                >
                  <IdCard
                    size={14}
                    className="text-foreground/50 mr-1.5 shrink-0"
                    aria-hidden="true"
                  />
                  KYC required
                </Link>
                <a
                  href="#money-back-guarantee"
                  onClick={handleMoneyBackClick}
                  className="border-border bg-muted/40 text-foreground hover:border-brand-green flex items-center rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors"
                >
                  <ShieldCheck
                    size={14}
                    className="text-foreground/50 mr-1.5 shrink-0"
                    aria-hidden="true"
                  />
                  30-day money-back guarantee
                  <span className="group/mbtooltip relative ml-0.5 cursor-help">
                    <span
                      className="text-base leading-none"
                      style={{ color: "var(--brand-green)" }}
                    >
                      *
                    </span>
                    <span className="text-foreground bg-popover border-border pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-72 -translate-x-1/2 rounded-lg border px-3 py-2 text-center text-xs leading-relaxed font-normal whitespace-normal normal-case opacity-0 shadow-lg transition-opacity duration-200 group-hover/mbtooltip:opacity-100">
                      All proxies come with a 30-day money-back guarantee when less than 10 GB has
                      been used.
                    </span>
                  </span>
                </a>
                <Link
                  href={ROUTES.contactSupport}
                  className="border-border bg-muted/40 text-foreground hover:border-brand-green hidden items-center rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors md:flex"
                >
                  <Headphones
                    size={14}
                    className="text-foreground/50 mr-1.5 shrink-0"
                    aria-hidden="true"
                  />
                  Premium support
                </Link>
              </div>
            )}
          </div>
        )}

        <div className={asHero ? "mx-auto mt-12 max-w-6xl" : "mx-auto max-w-6xl"}>
          <div className="mb-8 flex justify-center">
            <div
              role="tablist"
              aria-label="Pricing model"
              className="border-border bg-card/60 inline-flex items-center gap-1 rounded-full border p-1.5"
            >
              {orderedOptions.map((option) => {
                const isActive = option.id === plan;
                return (
                  <button
                    key={option.id}
                    role="tab"
                    type="button"
                    id={`${tablistId}-${option.id}-tab`}
                    aria-selected={isActive}
                    aria-controls={`${tablistId}-${option.id}-panel`}
                    onClick={() => setPlan(option.id)}
                    className={cn(
                      "focus-visible:ring-ring focus-visible:ring-offset-background cursor-pointer rounded-full px-5 py-2 text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div
            role="tabpanel"
            id={`${tablistId}-${plan}-panel`}
            aria-labelledby={`${tablistId}-${plan}-tab`}
          >
            {plan === "per-gb" ? <MobileTierGrid /> : <PerProxyCard />}
          </div>

          <p className="text-muted-foreground mt-6 text-center text-sm">
            Want the full plan details? Read the{" "}
            <a
              href={plan === "per-gb" ? DOCS.mobilePerGbPlan : DOCS.perProxyPlan}
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground font-medium underline underline-offset-4 transition-opacity hover:opacity-80"
            >
              {plan === "per-gb" ? "Per GB docs" : "Per Proxy docs"}
            </a>
            .
          </p>
        </div>
      </div>
    </section>
  );
}
